import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { getUserVote, likePostcard, rejectPostcard } from '../api/votes';

const VotesContext = createContext(null);

export const VotesProvider = ({ children }) => {
    const { user } = useAuth();
    const [userVotes, setUserVotes] = useState(new Map()); // relationId -> { has_vote, vote_type }
    const [loading, setLoading] = useState(false);

    // Загружаем голоса пользователя для открыток
    const loadUserVote = async (relationId) => {
        if (!user || !relationId) return;
        
        try {
            const voteData = await getUserVote(relationId, user.id);
            setUserVotes(prev => new Map(prev).set(relationId, voteData));
        } catch (error) {
            console.error(`Ошибка загрузки голоса для открытки ${relationId}:`, error);
        }
    };

    // Лайкнуть открытку
    const likePostcardAction = async (relationId) => {
        if (!user || !relationId) return false;
        
        try {
            setLoading(true);
            const result = await likePostcard(relationId, user.id);
            
            // Обновляем локальное состояние
            if (result.success) {
                const newVoteData = {
                    has_vote: result.action !== 'unliked',
                    vote_type: result.action === 'unliked' ? null : 'like'
                };
                setUserVotes(prev => new Map(prev).set(relationId, newVoteData));
            }
            
            return result;
        } catch (error) {
            console.error('Ошибка при лайке открытки:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Отклонить открытку
    const rejectPostcardAction = async (relationId) => {
        if (!user || !relationId) return false;
        
        try {
            setLoading(true);
            const result = await rejectPostcard(relationId, user.id);
            
            // Обновляем локальное состояние
            if (result.success) {
                const newVoteData = {
                    has_vote: result.action !== 'unrejected',
                    vote_type: result.action === 'unrejected' ? null : 'reject'
                };
                setUserVotes(prev => new Map(prev).set(relationId, newVoteData));
            }
            
            return result;
        } catch (error) {
            console.error('Ошибка при отклонении открытки:', error);
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Проверяем, лайкнул ли пользователь открытку
    const hasLiked = useMemo(() => {
        return (relationId) => {
            const vote = userVotes.get(relationId);
            return vote?.has_vote && vote?.vote_type === 'like';
        };
    }, [userVotes]);

    // Проверяем, отклонил ли пользователь открытку
    const hasRejected = useMemo(() => {
        return (relationId) => {
            const vote = userVotes.get(relationId);
            return vote?.has_vote && vote?.vote_type === 'reject';
        };
    }, [userVotes]);

    // Проверяем, голосовал ли пользователь за открытку
    const hasVoted = useMemo(() => {
        return (relationId) => {
            const vote = userVotes.get(relationId);
            return vote?.has_vote;
        };
    }, [userVotes]);

    const value = useMemo(() => ({
        userVotes,
        loading,
        loadUserVote,
        likePostcard: likePostcardAction,
        rejectPostcard: rejectPostcardAction,
        hasLiked,
        hasRejected,
        hasVoted
    }), [userVotes, loading, hasLiked, hasRejected, hasVoted]);

    return (
        <VotesContext.Provider value={value}>
            {children}
        </VotesContext.Provider>
    );
};

export const useVotes = () => {
    const ctx = useContext(VotesContext);
    if (!ctx) throw new Error('useVotes must be used within a VotesProvider');
    return ctx;
};
